import React from "react";
import { connect, useDispatch } from "react-redux";
import { Formik } from "formik";

import {
  Collapsible,
  CurrencyInput,
  DateInput,
  EditorField,
  GenderSelect,
  Select,
  SubmitButton,
  TextInput,
} from "components/forms";
import { Form } from "react-bootstrap";
import WSelect from "../../components/forms/selects/WindowedSelect";
import { activeAccountIdSelector, activeOpportunitySelector, managementChoicesSelector } from "../../reducers";
import { isEmpty } from "lodash";
import { partial } from "../../utils";
import { OPPORTUNITY_FILE_MODAL, OPPORTUNITY_MODAL } from "../../components/modals";
import * as notifications from "../../notifications";
import { createOpportunity, editOpportunity, fetchAccount, showModal } from "../../actions";
import { oppportunityStatusChoices, productTypeChoices, URLS } from "../../constants";
import MultipleFileDropZone from "../../components/MultipleFileDropZone";
import { FaUpload, FaUser } from "react-icons/fa";
import { selectOpportunityFile } from "../../reducers/life_settlement/management/opportunities";

const OpportunityForm = ({ opportunity, ...props }) => {
  const dispatch = useDispatch();
  let isSaved = opportunity && opportunity.id;
  let title = isSaved ? "Edit Opportunity" : "New Opportunity";

  const AddFileButton = () => {
    return (
      <button
        className="btn btn--secondary navbar-button"
        onClick={async () => {
          props.selectOpportunityFile(null);
          props.showModal(OPPORTUNITY_FILE_MODAL);
        }}
        style={{ marginLeft: "10px" }}
      >
        <FaUpload /> Add File
      </button>
    );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        account_id: props.activeAccountId,
        status: opportunity?.status || "In Dialogue",
        number_of_insured_lives: opportunity?.number_of_insured_lives || 1,
        sales_representative_id: opportunity?.sales_representative_id || props.userId,
        ...opportunity,
      }}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        const method =
          isEmpty(opportunity) || opportunity.id === undefined
            ? partial(props.createOpportunity, values)
            : partial(props.editOpportunity, opportunity.id, values);

        try {
          await method();
          // On Account, refresh the active account
          if (props.activeAccountId) {
            props.fetchAccount(props.activeAccountId);
          }
          dispatch(props.hideModal(OPPORTUNITY_MODAL));
        } catch (error) {
          setErrors(error.response.data);
          notifications.clearAll();
          notifications.error("Error saving Opportunity");
        }
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, values, setFieldValue }) => {
        return (
          <div>
            <Form onSubmit={handleSubmit} style={{ marginBottom: "-10px" }} {...props}>
              <Collapsible title={title} darker={true}>
                <Form.Row>
                  <WSelect
                    label="Select Account"
                    name="account_id"
                    options={props.accountChoices?.getChoices() || []}
                    placeholder="Select Account"
                    required={true}
                    disabled={props.activeAccountId}
                  />
                  <Select
                    label="Carrier"
                    name="carrier_id"
                    placeholder="Select Carrier"
                    options={props.carrierChoices?.getChoices() || []}
                  />
                </Form.Row>
                <Form.Row>
                  <CurrencyInput label="Face Amount" name="face_amount" />
                  <TextInput label="Policy Number" name="policy_number" />
                  <Select
                    label="Product Type"
                    name="product_type"
                    placeholder="Select Product Type"
                    options={productTypeChoices}
                  />
                </Form.Row>
                <Form.Row>
                  <Select
                    label="Status"
                    name="status"
                    placeholder="Select Status"
                    defaultValue={{ value: "In Dialogue", label: "In Dialogue" }}
                    options={oppportunityStatusChoices}
                    md={4}
                  />
                  <Select
                    label="Insured Lives"
                    name="number_of_insured_lives"
                    placeholder="Select Insured Lives"
                    defaultValue={{ value: 1, label: "Single" }}
                    options={[
                      { value: 1, label: "Single" },
                      { value: 2, label: "Joint Survivor" },
                    ]}
                    md={4}
                  />
                </Form.Row>
                <div className="form-label" style={{ margin: "20px 0 10px" }}>
                  <span className="form-label__label" style={{ marginLeft: "calc(1rem + 5px)" }}>
                    Insured 1
                  </span>
                </div>
                <Form.Row>
                  <TextInput label="First Name" name="insured_1_first_name" />
                  <TextInput label="Last Name" name="insured_1_last_name" />
                </Form.Row>
                <Form.Row>
                  <GenderSelect name="insured_1_gender" />
                  <DateInput label="DOB" name="insured_1_dob" />
                  <TextInput label="Age" name="insured_1_age" />
                </Form.Row>
                {values.number_of_insured_lives === 2 && (
                  <>
                    <div className="form-label" style={{ margin: "20px 0 10px" }}>
                      <span className="form-label__label" style={{ marginLeft: "calc(1rem + 5px)" }}>
                        Insured 2
                      </span>
                    </div>
                    <Form.Row>
                      <TextInput label="First Name" name="insured_2_first_name" />
                      <TextInput label="Last Name" name="insured_2_last_name" />
                    </Form.Row>
                    <Form.Row>
                      <GenderSelect name="insured_2_gender" />
                      <DateInput label="DOB" name="insured_2_dob" />
                      <DateInput label="Age" name="insured_2_age" />
                    </Form.Row>
                  </>
                )}
                <Form.Row>
                  <EditorField
                    style={{ width: "100%", marginTop: 15 }}
                    inline={false}
                    autoFocus={false}
                    label="Notes"
                    name="notes"
                  />
                </Form.Row>
                <br />
                {isSaved ? null : <MultipleFileDropZone setFieldValue={setFieldValue} />}
                <SubmitButton />
                {isSaved ? <AddFileButton /> : null}
              </Collapsible>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = state => {
  const choices = managementChoicesSelector(state);
  const inAccountDetail = window.location.pathname.includes(URLS.ACCOUNTS);
  const activeAccountId = activeAccountIdSelector(state);
  const currentOpportunity = activeOpportunitySelector(state) || {};

  return {
    activeAccountId: inAccountDetail && Number(activeAccountId),
    carrierChoices: choices.carriers,
    accountChoices: choices.accounts,
    nationalAccountChoices: choices.national_accounts,
    salesRepChoices: choices.case_sales_representatives,
    userId: state.auth.user_id,
    opportunity: currentOpportunity,
  };
};

export default connect(mapStateToProps, {
  createOpportunity,
  editOpportunity,
  fetchAccount,
  selectOpportunityFile,
  showModal,
})(OpportunityForm);
